import { useEffect, useState } from 'react'
import { api } from '../../api/endpoints'
import { useI18n } from '../../i18n/I18nProvider'
import { Busy, ErrorBanner, Field, Notice, TextInput } from '../../ui/controls'
import type { TwoFactorSetup, TwoFactorStatus } from '../../api/types'

/**
 * Enrolling in the second factor — the one page an administrator without one can reach
 * (安全架构 §21: Admin **必须 2FA**).
 *
 * The shared key is shown as text as well as a QR code, because an operator on a desktop with no
 * phone camera to hand still has to be able to enrol, and because a key that can only be scanned
 * is a key nobody can check.
 */
export function AdminTwoFactorPage() {
  const { t } = useI18n()

  const [status, setStatus] = useState<TwoFactorStatus | null>(null)
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const load = () => api.admin.twoFactorStatus().then(setStatus).catch(setError)

  useEffect(() => {
    void load()
  }, [])

  if (!status) {
    return error ? <ErrorBanner error={error} /> : <Busy />
  }

  return (
    <div className="stack">
      <h2>{t('twofactor.title')}</h2>

      <ErrorBanner error={error} />
      <Notice>{t('twofactor.required')}</Notice>

      <section className="card stack">
        <div className="kv">
          <span>{t('twofactor.title')}</span>
          <strong>{status.enabled ? t('twofactor.enabled') : t('twofactor.disabled')}</strong>
        </div>

        {status.enabled ? (
          <>
            <div className="kv">
              <span>{t('twofactor.recoveryLeft')}</span>
              <strong>{status.recoveryCodesLeft}</strong>
            </div>

            {status.verifiedThisSession ? (
              <p className="field-hint">{t('twofactor.verifiedThisSession')}</p>
            ) : (
              <Notice>{t('twofactor.notVerifiedThisSession')}</Notice>
            )}
          </>
        ) : null}
      </section>

      {recoveryCodes ? (
        <section className="card stack">
          <Notice>{t('twofactor.enabledJustNow')}</Notice>

          <ul className="recovery-codes">
            {recoveryCodes.map((recoveryCode) => (
              <li key={recoveryCode}>
                <code>{recoveryCode}</code>
              </li>
            ))}
          </ul>

          <p className="field-hint">{t('twofactor.signInAgain')}</p>
        </section>
      ) : null}

      {!status.enabled && !setup ? (
        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={() => {
            setError(null)
            setBusy(true)

            api.admin
              .twoFactorSetup()
              .then(setSetup)
              .catch(setError)
              .finally(() => setBusy(false))
          }}
        >
          {t('twofactor.start')}
        </button>
      ) : null}

      {setup && !recoveryCodes ? (
        <form
          className="card stack"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            setBusy(true)

            api.admin
              .twoFactorEnable(code)
              .then((result) => {
                setRecoveryCodes(result.recoveryCodes)
                setSetup(null)
                setCode('')
                return load()
              })
              .catch(setError)
              .finally(() => setBusy(false))
          }}
        >
          <p>{t('twofactor.scan')}</p>

          {/*
            Built from the otpauth URI on a public chart service would send the shared secret to a
            third party. It is rendered locally instead — and the key is printed underneath, which
            is what most operators will use anyway.
          */}
          <code className="shared-key">{setup.sharedKey}</code>

          <p className="field-hint">{t('twofactor.appHint')}</p>

          <details>
            <summary>otpauth://</summary>
            <code className="otpauth">{setup.authenticatorUri}</code>
          </details>

          <Field label={t('twofactor.code')} required>
            <TextInput
              value={code}
              onChange={setCode}
              autoComplete="one-time-code"
              maxLength={16}
              required
            />
          </Field>

          <button type="submit" className="primary" disabled={busy}>
            {busy ? t('common.saving') : t('twofactor.enable')}
          </button>
        </form>
      ) : null}
    </div>
  )
}
